import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  cartAllProductsSelector,
  cartAppliedCouponsSelector,
  cartHomeDeliverySelector,
  cartIdSelector,
  cartIsPickupTypeByPickupSelector,
  cartPickupStoreSelector,
  cartPricesSelector,
  checkoutBillingAddressSelector,
  checkoutCheckoutEmailSelector,
  checkoutShippingAddressesSelector,
  pickupPersonInformationSchema,
  resolveCatchMessage,
  setSelectedShippingMethod,
  setShippingAddresses,
  TAddressFormValues,
  TCatchMessage,
  TEmailSchema,
  TPickupPersonInformation,
  updateCartTotalsInformation,
  useCountries,
  useLoadingContext,
  userAddressAllSelector,
  userIsLoggedInSelector,
  useSetBillingAddressesOnCartMutation,
  useSetGuestEmailOnCartMutation,
  useSetShippingAddressesOnCartMutation,
  useToastContext,
  useValidateCustomerAddressMutation,
} from '@segway/core'
import useCart from '@segway/core/src/businessHooks/useCart'
import {
  CartAddressInput,
  CountryCodeEnum,
  CustomerAddressInput,
  TotalsInformationMutationVariables,
} from '@segway/core/src/graphql/generated/graphql'
import { useSaveAddressInformationMutation } from '@segway/core/src/services/client/checkoutApi'
import { useAppDispatch, useAppSelector } from '@segway/core/src/store/hooks'
import { useForm } from 'react-hook-form'

import { GuestPickupEmailRef } from '@/app/[locale]/checkout/_components/checkout/guestPickupEmail/GuestPickupEmail'
import { AddressFieldComparison, compareAddresses } from '@/app/[locale]/customer/address/_utils'
import { UserAddressFormRef } from '@/components/userAddressForm/UserAddressForm'
import { generateGTMCartItemsParams } from '@/utils'
import reportGTMEvent from '@/utils/gtm'

/**
 * 结算页联系信息 Hooks
 */
const usePickupPersonInformation = () => {
  const billingFormRef = useRef<UserAddressFormRef>(null)
  const shippingFormRef = useRef<UserAddressFormRef>(null)
  const guestEmailFormRef = useRef<GuestPickupEmailRef>(null)

  // Pickup表单
  const form = useForm<TPickupPersonInformation>({
    resolver: zodResolver(pickupPersonInformationSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      telephone: '',
    },
  })
  const dispatch = useAppDispatch()
  const loading = useLoadingContext()
  const toast = useToastContext()
  const { totalsInformation } = useCart()

  const [saveAddressInformationMutation] = useSaveAddressInformationMutation()
  const [setGuestEmailOnCartMutation] = useSetGuestEmailOnCartMutation()
  const [setShippingAddressesOnCartMutation] = useSetShippingAddressesOnCartMutation()
  const [setBillingAddressesOnCartMutation] = useSetBillingAddressesOnCartMutation()
  const [validateCustomerAddressMutation] = useValidateCustomerAddressMutation()
  const cartId = useAppSelector(cartIdSelector)
  const shippingAddresses = useAppSelector(checkoutShippingAddressesSelector)
  const billingAddresses = useAppSelector(checkoutBillingAddressSelector)
  const userAddressAll = useAppSelector(userAddressAllSelector)
  const cartStore = useAppSelector(cartPickupStoreSelector)
  const isLoggedIn = useAppSelector(userIsLoggedInSelector)
  const isPickupTypeByPickup = useAppSelector(cartIsPickupTypeByPickupSelector)
  const cartHomeDelivery = useAppSelector(cartHomeDeliverySelector)
  const checkoutEmail = useAppSelector(checkoutCheckoutEmailSelector)
  const { getRegionNameById, getRegionCodeById } = useCountries()
  const cartPrices = useAppSelector(cartPricesSelector)
  const appliedCoupons = useAppSelector(cartAppliedCouponsSelector)
  const allProducts = useAppSelector(cartAllProductsSelector)

  const [isRender, setIsRender] = useState<boolean>(false)
  const [pickupValues, setPickupValues] = useState<TPickupPersonInformation | null>(null)
  const [homeDeliveryShippingValues, setHomeDeliveryShippingValues] =
    useState<CartAddressInput | null>(null)
  const [billingValues, setBillingValues] = useState<CartAddressInput | null>(null)
  const [isPaymentStep, setIsPaymentStep] = useState<boolean>(false)
  const [paymentMethods, setPaymentMethods] = useState<
    { code: string; title: string }[] | undefined
  >([])
  const [addressValidator, setAddressValidator] = useState<{
    modalVisible: boolean
    address: TAddressFormValues | null
    improvedAddress?: CustomerAddressInput | null
    addressComparison?: AddressFieldComparison[] | null
    saveCallback?: (address: CustomerAddressInput) => void
    errorMessage?: string
    title?: string
  }>({
    modalVisible: false,
    address: null,
    improvedAddress: null,
    addressComparison: null,
    saveCallback: () => {},
    errorMessage: '',
    title: '',
  })

  const isHomeDelivery = useMemo(() => {
    return isPickupTypeByPickup && cartHomeDelivery
  }, [cartHomeDelivery, isPickupTypeByPickup])

  const isPickup = useMemo(() => {
    return isPickupTypeByPickup && !cartHomeDelivery
  }, [cartHomeDelivery, isPickupTypeByPickup])

  const pickupPersonInformationVisible = useMemo(() => {
    return isPickup && !isPaymentStep
  }, [isPickup, isPaymentStep])

  const shippingVisible = useMemo(() => {
    return isHomeDelivery && !isPaymentStep
  }, [isHomeDelivery, isPaymentStep])

  const billingVisible = useMemo(() => {
    return !isPaymentStep
  }, [isPaymentStep])

  const closeAddressValidator = useCallback(() => {
    setAddressValidator({
      ...addressValidator,
      modalVisible: false,
    })
  }, [addressValidator])

  /**
   * Save shipping address
   */
  const saveShippingAddress = useCallback(
    async (address: CartAddressInput) => {
      try {
        const res = await setShippingAddressesOnCartMutation({
          input: {
            cart_id: cartId,
            shipping_addresses: [
              {
                address,
              },
            ],
          },
        })
        // 返回运输方式
        const availableShippingMethods =
          res?.data?.setShippingAddressesOnCart?.cart?.shipping_addresses?.[0]
            ?.available_shipping_methods ?? []
        const availableMethod = availableShippingMethods.find((method) => {
          return method?.available && method?.carrier_code && method?.method_code
        })
        if (availableMethod?.carrier_code && availableMethod?.method_code) {
          dispatch(setSelectedShippingMethod(availableMethod))
          return availableMethod
        }
        toast.show({
          icon: 'fail',
          content: 'There is no available shipping method',
        })
        return null
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
        return null
      }
    },
    [cartId, setShippingAddressesOnCartMutation, toast, dispatch],
  )

  /**
   * Save billing address
   */
  const saveBillingAddress = useCallback(
    async (address: CartAddressInput) => {
      try {
        const res = await setBillingAddressesOnCartMutation({
          input: {
            cart_id: cartId,
            billing_address: {
              address,
            },
          },
        })
        if (res?.data?.setBillingAddressOnCart?.cart) {
          return res.data.setBillingAddressOnCart.cart
        }
        toast.show({
          icon: 'fail',
          content: 'Save billing failed',
        })
        return null
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
        return null
      }
    },
    [cartId, setBillingAddressesOnCartMutation, toast],
  )

  /**
   * 设置购物车试算信息
   */
  const saveTotalsInformation = useCallback(
    async (
      inputValues: Omit<NonNullable<TotalsInformationMutationVariables['input']>, 'cartId'>,
    ) => {
      try {
        const res = await totalsInformation(inputValues)
        if (res) {
          dispatch(updateCartTotalsInformation(res))
          return res
        }
        toast.show({
          icon: 'fail',
          content: 'Save totals information error',
        })
        return null
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
        return null
      }
    },
    [dispatch, toast, totalsInformation],
  )

  /**
   * 保存游客邮箱
   */
  const handleSaveGuestEmail = useCallback(
    async (email: string) => {
      try {
        if (isLoggedIn) {
          return true
        }
        const res = await setGuestEmailOnCartMutation({
          input: {
            cart_id: cartId,
            email,
          },
        })
        if (res?.data?.setGuestEmailOnCart?.cart) {
          return res.data.setGuestEmailOnCart.cart
        }
        toast.show({
          icon: 'fail',
          content: 'Save email error',
        })
        return null
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
        return e
      }
    },
    [cartId, setGuestEmailOnCartMutation, toast, isLoggedIn],
  )

  /**
   * 提交 Pickup/Home Delivery 信息：pickup表单、Shipping表单、Billing表单、设置游客邮箱（如果没登录）
   */
  const handleSubmitInformation = useCallback(
    async ({
      homeDeliveryGuestEmail,
      pickupFormValues,
      shippingFormValues,
      billingFormValues,
    }: {
      homeDeliveryGuestEmail?: string
      pickupFormValues?: TPickupPersonInformation | null
      shippingFormValues?: CartAddressInput
      billingFormValues: CartAddressInput
    }) => {
      try {
        if (isPickup && (!pickupFormValues || !billingFormValues || !cartStore)) {
          return
        }
        if (!isPickup && (!shippingFormValues || !billingFormValues || !cartStore)) {
          return
        }
        loading.show()
        // Save guest email
        const guestEmail = isPickup ? (pickupFormValues?.email ?? '') : homeDeliveryGuestEmail || ''
        const saveGuestEmailRes = await handleSaveGuestEmail(guestEmail)
        if (!saveGuestEmailRes) {
          loading.hide()
          return
        }
        const storeAddress = cartStore?.address?.split(',') ?? []
        // Set shipping address
        const setShippingRes = await saveShippingAddress({
          city: cartStore?.city ?? '',
          company: '', // cartStore中没有company数据
          country_code: cartStore?.country_id as CountryCodeEnum,
          firstname: cartStore?.store_name ?? '',
          lastname: cartStore?.store_name ?? '',
          postcode: cartStore?.zipcode ?? '',
          region: cartStore?.region?.code ?? '',
          region_id: Number(cartStore?.region?.id),
          save_in_address_book: false,
          street: [storeAddress?.[0] ?? '', storeAddress?.[1] ?? '', storeAddress?.[2] ?? ''],
          telephone: cartStore?.telephone ?? '',
        })
        if (!setShippingRes?.carrier_code || !setShippingRes?.method_code) {
          loading.hide()
          return
        }
        const { carrier_code, method_code, method_title } = setShippingRes

        // Set billing address
        const setBillingRes = await saveBillingAddress({
          ...billingFormValues,
          save_in_address_book: billingFormValues?.save_in_address_book ?? false,
        })
        if (setBillingRes) {
          // 保存结算页信息
          dispatch(setShippingAddresses(setBillingRes?.shipping_addresses))
        } else {
          loading.hide()
          return
        }

        // 设置购物车试算信息
        const totalsInformationRes = await saveTotalsInformation({
          address: isPickup
            ? {
                email: cartStore?.store_email ?? '',
                country_id: cartStore?.country_id as CountryCodeEnum,
                region_id: Number(cartStore?.region?.id),
                region: cartStore?.region?.name,
                street: [storeAddress?.[0] ?? '', storeAddress?.[1] ?? '', storeAddress?.[2] ?? ''],
                company: '', // cartStore中没有company数据
                telephone: cartStore?.telephone ?? '',
                postcode: cartStore?.zipcode ?? '',
                city: cartStore?.city ?? '',
                firstname: cartStore?.store_name ?? '',
                lastname: cartStore?.store_name ?? '',
                save_in_address_book: 0,
              }
            : {
                email: isLoggedIn ? checkoutEmail : homeDeliveryGuestEmail,
                country_id: shippingFormValues?.country_code,
                region_id: shippingFormValues?.region_id,
                region: shippingFormValues?.region,
                street: shippingFormValues?.street,
                company: shippingFormValues?.company ?? '',
                telephone: shippingFormValues?.telephone ?? '',
                postcode: shippingFormValues?.postcode ?? '',
                city: shippingFormValues?.city ?? '',
                firstname: shippingFormValues?.firstname ?? '',
                lastname: shippingFormValues?.lastname ?? '',
                save_in_address_book: 0,
              },
          shipping_carrier_code: carrier_code,
          shipping_method_code: method_code,
        })
        if (!totalsInformationRes) {
          toast.show({
            icon: 'fail',
            content: '更新购物车结算信息失败',
          })
          loading.hide()
          return
        }

        // 设置结算信息
        const extension_attributes = isPickup
          ? {
              pickup_store: cartStore?.id,
              pickup_phone: pickupFormValues?.telephone ?? '',
              pickup_email: pickupFormValues?.email ?? '',
              pickup_firstname: pickupFormValues?.firstname ?? '',
              pickup_lastname: pickupFormValues?.lastname ?? '',
            }
          : {
              pickup_store: cartStore?.id,
              home_delivery: true,
              delivery_address: {
                firstname: shippingFormValues?.firstname ?? '',
                lastname: shippingFormValues?.lastname ?? '',
                street: [
                  shippingFormValues?.street?.[0] ?? '',
                  shippingFormValues?.street?.[1] ?? '',
                  shippingFormValues?.street?.[2] ?? '',
                ],
                city: shippingFormValues?.city ?? '',
                region: shippingFormValues?.region,
                postcode: shippingFormValues?.postcode ?? '',
                country_id: shippingFormValues?.country_code as CountryCodeEnum,
                telephone: shippingFormValues?.telephone ?? '',
                default_billing: false,
                default_shipping: false,
                region_id: shippingFormValues?.region_id,
              },
            }
        const saveAddressInformationRes = await saveAddressInformationMutation({
          cartId,
          input: {
            shipping_carrier_code: carrier_code,
            shipping_method_code: method_code,
            shipping_address: {
              email: cartStore?.store_email ?? '',
              country_id: cartStore?.country_id as CountryCodeEnum,
              region_id: Number(cartStore?.region?.id),
              region_code: cartStore?.region?.code,
              region: cartStore?.region?.name,
              street: [storeAddress?.[0] ?? '', storeAddress?.[1] ?? '', storeAddress?.[2] ?? ''],
              company: '',
              telephone: cartStore?.telephone ?? '',
              postcode: cartStore?.zipcode ?? '',
              city: cartStore?.city ?? '',
              firstname: cartStore?.store_name ?? '',
              lastname: cartStore?.store_name ?? '',
              save_in_address_book: 0,
            },
            billing_address: {
              country_id: billingFormValues.country_code as CountryCodeEnum,
              region_id: Number(billingFormValues.region_id),
              region_code: billingFormValues.region,
              region: '',
              street: [
                billingFormValues?.street[0] || '',
                billingFormValues?.street[1] || '',
                billingFormValues?.street[2] || '',
              ],
              company: billingFormValues.company,
              telephone: billingFormValues.telephone,
              postcode: billingFormValues.postcode,
              city: billingFormValues.city,
              firstname: billingFormValues.firstname,
              lastname: billingFormValues.lastname,
              same_as_billing: 1,
            },
            extension_attributes,
          },
        })
        if (saveAddressInformationRes?.error) {
          toast.show({
            icon: 'fail',
            content: resolveCatchMessage(saveAddressInformationRes.error as TCatchMessage),
          })
          loading.hide()
          return
        }
        // 保存支付方式列表
        if (saveAddressInformationRes?.data?.saveAddressInformation?.payment_methods) {
          /**
           * GTM: add_shipping_info
           */
          reportGTMEvent({
            event: 'add_shipping_info',
            ecommerce: {
              currency: cartPrices?.grand_total?.currency,
              value: cartPrices?.subtotal_excluding_tax?.value,
              shipping_tier: method_title,
              coupon: appliedCoupons && appliedCoupons?.length ? appliedCoupons[0]?.code : '',
              items: generateGTMCartItemsParams(allProducts),
            },
          })

          setPaymentMethods(
            saveAddressInformationRes.data.saveAddressInformation.payment_methods?.map((method) => {
              return {
                code: method?.code ?? '',
                title: method?.title ?? '',
              }
            }),
          )
          // 下一步, 显示支付方式
          setIsPaymentStep(true)
        } else {
          toast.show({
            icon: 'fail',
            content: 'Save address information error',
          })
        }
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [
      dispatch,
      cartId,
      cartStore,
      handleSaveGuestEmail,
      loading,
      saveAddressInformationMutation,
      saveBillingAddress,
      saveShippingAddress,
      saveTotalsInformation,
      toast,
      isPickup,
      checkoutEmail,
      isLoggedIn,
      allProducts,
      appliedCoupons,
      cartPrices,
    ],
  )

  /**
   * 设置默认地址的结算信息
   */
  const saveDefaultTotalsInformation = useCallback(
    async (
      inputValues: Omit<NonNullable<TotalsInformationMutationVariables['input']>, 'cartId'>,
    ) => {
      loading.show()
      try {
        await saveTotalsInformation(inputValues)
      } catch (e) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(e as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [loading, saveTotalsInformation, toast],
  )

  /**
   * 地址格式化
   */
  const getCartAddressValues = useCallback((address: TAddressFormValues): CartAddressInput => {
    return {
      ...address,
      region_id: Number(address.region_id),
      street: [address.street[0], address.street[1], address?.street?.[2] || ''],
    }
  }, [])

  /**
   * 地址格式化,将校验返回的地址格式化为Form表单地址, 不包含国家
   */
  const getBaseFormValuesByAddressSelected = useCallback(
    (address: CustomerAddressInput): TAddressFormValues => {
      // 不包含国家
      return {
        city: address?.city ?? '',
        company: address?.company ?? '',
        firstname: address?.firstname ?? '',
        lastname: address?.lastname ?? '',
        postcode: address?.postcode ?? '',
        region: getRegionCodeById(Number(address?.region?.region_id)),
        region_id: String(address?.region?.region_id),
        telephone: address?.telephone ?? '',
        street: [
          address?.street?.[0] ?? '',
          address?.street?.[1] ?? '',
          address?.street?.[2] ?? '',
        ],
      } as TAddressFormValues
    },
    [getRegionCodeById],
  )

  /**
   * 返回上一步
   */
  const handleBack = useCallback(() => {
    setIsPaymentStep(false)
  }, [])

  /**
   * 返回上一步
   */
  const getValidateAddressQueryByFormValues = useCallback(
    (address: CartAddressInput) => {
      return {
        city: address?.city ?? '',
        company: address?.company ?? '',
        country_code: address?.country_code as CountryCodeEnum,
        firstname: address?.firstname ?? '',
        lastname: address?.lastname ?? '',
        postcode: address?.postcode ?? '',
        region: {
          region_id: Number(address?.region_id),
          region_code: address?.region,
          region: getRegionNameById(Number(address?.region_id)),
        },
        street: address.street,
        telephone: address?.telephone ?? '',
      }
    },
    [getRegionNameById],
  )

  /**
   *  提交Home Delivery 的 shipping 和 billing
   */
  const submitHomeDeliveryShippingAndBilling = useCallback(
    (guestEmailFormValues: TEmailSchema | null) => {
      if (shippingFormRef?.current) {
        // Shipping表单校验
        shippingFormRef.current.handleSubmit(async (shippingFormValues: TAddressFormValues) => {
          const shippingFormValuesFormat = getCartAddressValues(shippingFormValues)
          setHomeDeliveryShippingValues(shippingFormValuesFormat)
          if (billingFormRef?.current) {
            // Billing表单校验
            billingFormRef.current.handleSubmit(async (billingFormValues: TAddressFormValues) => {
              // 类型匹配：TAddressFormValues => CartAddressInput
              const billingFormValuesFormat = getCartAddressValues(billingFormValues)
              setBillingValues(billingFormValuesFormat)
              // 提交
              if (shippingFormValuesFormat && billingFormValues) {
                // 地址校验 Home Delivery
                try {
                  loading.show()
                  // Shipping 地址校验, 如果通过，继续校验 Billing 地址
                  const curShippingQuery =
                    getValidateAddressQueryByFormValues(shippingFormValuesFormat)
                  const validateShippingRes = await validateCustomerAddressMutation({
                    input: curShippingQuery,
                  })
                  loading.hide()
                  // Billing 地址校验函数(Shipping地址校验后, 接收一个参数未校验后选择的Shipping的地址)
                  const handlevalidateBilling = (finalShippingValues: CartAddressInput) => {
                    const validateBilling = async () => {
                      loading.show()
                      const curBillingQuery =
                        getValidateAddressQueryByFormValues(billingFormValuesFormat)
                      const validateBillingRes = await validateCustomerAddressMutation({
                        input: curBillingQuery,
                      })
                      loading.hide()
                      // Billing 地址校验失败, 弹窗提示
                      if (validateBillingRes?.error) {
                        setAddressValidator({
                          address: billingFormValues,
                          modalVisible: true,
                          errorMessage: `Billing Address: ${resolveCatchMessage(validateBillingRes?.error as TCatchMessage)}`,
                        })
                      } else {
                        // Billing 地址校验通过, 对比地址
                        const curBilling: CustomerAddressInput = { ...curBillingQuery }
                        curBilling.country_id = curShippingQuery.country_code
                        delete curBilling.country_code
                        const improvedAddress = validateBillingRes?.data
                          ?.validateAddress as CustomerAddressInput
                        if (improvedAddress) {
                          const addressComparison = compareAddresses(
                            curBilling,
                            improvedAddress as CustomerAddressInput,
                          )
                          // 判断是否有推荐的地址
                          const isAddressNeedToImprove = addressComparison?.some(
                            (addressItem) => addressItem.isDifferent,
                          )
                          // 如果有推荐地址，弹窗展示推荐的 Billing 地址
                          if (isAddressNeedToImprove) {
                            loading.hide()
                            setAddressValidator({
                              address: billingFormValues, // Form values
                              improvedAddress,
                              addressComparison,
                              modalVisible: true,
                              title: 'Billing Address Suggestion',
                              saveCallback: (addressSelected: CustomerAddressInput) => {
                                const baseFormValues =
                                  getBaseFormValuesByAddressSelected(addressSelected)
                                // 设置Billing表单，国家不重置
                                if (billingFormRef?.current?.resetForm) {
                                  billingFormRef.current.resetForm(baseFormValues)
                                }
                                const finalBillingFormValues = {
                                  ...billingFormValuesFormat,
                                  ...baseFormValues,
                                  region_id: addressSelected?.region?.region_id,
                                  country_code: addressSelected.country_id as CountryCodeEnum,
                                }
                                setBillingValues(finalBillingFormValues)

                                // 提交
                                handleSubmitInformation({
                                  homeDeliveryGuestEmail: guestEmailFormValues?.email ?? '',
                                  shippingFormValues: finalShippingValues,
                                  billingFormValues: finalBillingFormValues,
                                })
                              },
                            })
                          } else {
                            // 地址一致，提交
                            handleSubmitInformation({
                              homeDeliveryGuestEmail: guestEmailFormValues?.email ?? '',
                              shippingFormValues: finalShippingValues,
                              billingFormValues: billingFormValuesFormat,
                            })
                          }
                        } else {
                          toast.show({
                            icon: 'fail',
                            content: 'Validate billing address failed',
                          })
                          loading.hide()
                        }
                      }
                    }
                    validateBilling()
                  }

                  // Shipping 地址校验失败, 弹窗提示
                  if (validateShippingRes?.error) {
                    setAddressValidator({
                      address: shippingFormValues,
                      modalVisible: true,
                      errorMessage: `Shipping Address: ${resolveCatchMessage(validateShippingRes?.error as TCatchMessage)}`,
                    })
                  } else {
                    // Shipping 地址校验通过, 对比地址
                    const curShipping: CustomerAddressInput = { ...curShippingQuery }
                    curShipping.country_id = curShippingQuery.country_code
                    delete curShipping.country_code
                    const improvedAddress = validateShippingRes?.data
                      ?.validateAddress as CustomerAddressInput
                    if (improvedAddress) {
                      const addressComparison = compareAddresses(
                        curShipping,
                        improvedAddress as CustomerAddressInput,
                      )
                      // 判断是否有推荐的地址
                      const isAddressNeedToImprove = addressComparison?.some(
                        (addressItem) => addressItem.isDifferent,
                      )
                      // 如果有推荐地址，弹窗展示推荐的 Shipping 地址
                      if (isAddressNeedToImprove) {
                        loading.hide()
                        setAddressValidator({
                          address: shippingFormValues, // Form values
                          improvedAddress,
                          addressComparison,
                          modalVisible: true,
                          title: 'Shipping Address Suggestion',
                          saveCallback: (addressSelected: CustomerAddressInput) => {
                            const baseFormValues =
                              getBaseFormValuesByAddressSelected(addressSelected)
                            // 设置Shipping表单，国家不重置
                            if (shippingFormRef?.current?.resetForm) {
                              shippingFormRef.current.resetForm(baseFormValues)
                            }
                            const finalShippingFormValues = {
                              ...shippingFormValuesFormat,
                              ...baseFormValues,
                              region_id: addressSelected?.region?.region_id,
                              country_code: addressSelected.country_id as CountryCodeEnum,
                            }
                            setHomeDeliveryShippingValues(finalShippingFormValues)
                            // 校验Billing地址
                            handlevalidateBilling(finalShippingFormValues)
                          },
                        })
                      } else {
                        // 地址一致，校验Billing地址
                        handlevalidateBilling(shippingFormValuesFormat)
                      }
                    } else {
                      toast.show({
                        icon: 'fail',
                        content: 'Validate shipping address failed',
                      })
                      loading.hide()
                    }
                  }
                } catch (e) {
                  loading.hide()
                  toast.show({
                    icon: 'fail',
                    content: resolveCatchMessage(e as TCatchMessage),
                  })
                }
              }
            })
          }
        })
      }
    },
    [
      loading,
      getCartAddressValues,
      getValidateAddressQueryByFormValues,
      handleSubmitInformation,
      toast,
      validateCustomerAddressMutation,
      getBaseFormValuesByAddressSelected,
    ],
  )

  /**
   * 点击下一步
   */
  const handleNext = useCallback(() => {
    if (isPickup) {
      // Pickup表单校验
      form.handleSubmit((pickupFormValues) => {
        setPickupValues(pickupFormValues)
        if (billingFormRef?.current) {
          // Billing表单校验
          billingFormRef.current.handleSubmit(async (billingFormValues: TAddressFormValues) => {
            // 类型匹配：TAddressFormValues => CartAddressInput
            const billingFormValuesFormat = getCartAddressValues(billingFormValues)
            setBillingValues(billingFormValuesFormat)
            if (pickupFormValues && billingFormValues) {
              // Pickup: Billing地址校验
              try {
                loading.show()
                const address = getValidateAddressQueryByFormValues(billingFormValuesFormat)
                const validateBillingRes = await validateCustomerAddressMutation({
                  input: address,
                })
                // 地址校验失败
                if (validateBillingRes?.error) {
                  setAddressValidator({
                    address: billingFormValues,
                    modalVisible: true,
                    errorMessage: `Billing Address: ${resolveCatchMessage(validateBillingRes?.error as TCatchMessage)}`,
                  })
                  loading.hide()
                } else {
                  // 地址校验成功，对比地址
                  const curAddress: CustomerAddressInput = { ...address }
                  curAddress.country_id = address.country_code
                  delete curAddress.country_code
                  const improvedAddress = validateBillingRes?.data
                    ?.validateAddress as CustomerAddressInput
                  if (improvedAddress) {
                    const addressComparison = compareAddresses(
                      curAddress,
                      improvedAddress as CustomerAddressInput,
                    )
                    // 判断是否有推荐的地址
                    const isAddressNeedToImprove = addressComparison?.some(
                      (addressItem) => addressItem.isDifferent,
                    )
                    // 弹窗展示推荐的 Billing 地址
                    if (isAddressNeedToImprove) {
                      loading.hide()
                      setAddressValidator({
                        address: billingFormValues, // Form values
                        improvedAddress,
                        addressComparison,
                        modalVisible: true,
                        title: 'Billing Address Suggestion',
                        saveCallback: (addressSelected: CustomerAddressInput) => {
                          const baseFormValues = getBaseFormValuesByAddressSelected(addressSelected)
                          // 设置表单，国家不重置
                          if (billingFormRef?.current?.resetForm) {
                            billingFormRef.current.resetForm(baseFormValues as TAddressFormValues)
                          }
                          const finalBillingFormValues = {
                            ...billingFormValuesFormat,
                            ...baseFormValues,
                            region_id: addressSelected?.region?.region_id,
                            country_code: addressSelected.country_id as CountryCodeEnum,
                          }
                          setBillingValues(finalBillingFormValues)
                          // 提交表单
                          handleSubmitInformation({
                            pickupFormValues,
                            billingFormValues: finalBillingFormValues,
                          })
                        },
                      })
                    } else {
                      // 地址一致，提交表单
                      handleSubmitInformation({
                        pickupFormValues,
                        billingFormValues: billingFormValuesFormat,
                      })
                    }
                  } else {
                    toast.show({
                      icon: 'fail',
                      content: 'Validate address failed',
                    })
                    loading.hide()
                  }
                }
              } catch (e) {
                loading.hide()
                toast.show({
                  icon: 'fail',
                  content: resolveCatchMessage(e as TCatchMessage),
                })
              }
            }
          })
        }
      })()
    } else {
      if (guestEmailFormRef?.current) {
        if (isLoggedIn) {
          submitHomeDeliveryShippingAndBilling(null)
        } else {
          // 游客：验证输入的邮箱
          guestEmailFormRef.current.handleSubmit(async (guestEmailFormValues: TEmailSchema) => {
            submitHomeDeliveryShippingAndBilling(guestEmailFormValues)
          })
        }
      }
    }
  }, [
    loading,
    isPickup,
    form,
    getCartAddressValues,
    validateCustomerAddressMutation,
    getValidateAddressQueryByFormValues,
    handleSubmitInformation,
    toast,
    isLoggedIn,
    submitHomeDeliveryShippingAndBilling,
    getBaseFormValuesByAddressSelected,
  ])

  useEffect(() => {
    if (isRender) {
      return
    }
    setIsRender(true)
    const defaultItem = userAddressAll.find((item) => item.default_shipping)
    const defaultUserAddress = defaultItem || userAddressAll[0] || null
    if (isHomeDelivery) {
      // 设置默认的 shipping 地址为 home delivery 地址
      if (shippingAddresses && shippingAddresses.length > 0) {
        const defaultShipping = shippingAddresses[0]
        saveDefaultTotalsInformation({
          address: {
            email: checkoutEmail,
            country_id: defaultShipping?.country?.code as CountryCodeEnum,
            region_id: defaultShipping?.region?.region_id,
            region: defaultShipping?.region?.code,
            street: [
              defaultShipping?.street?.[0] ?? '',
              defaultShipping?.street?.[1] ?? '',
              defaultShipping?.street?.[2] ?? '',
            ],
            company: defaultShipping?.company ?? '',
            telephone: defaultShipping?.telephone ?? '',
            postcode: defaultShipping?.postcode ?? '',
            city: defaultShipping?.city ?? '',
            firstname: defaultShipping?.firstname ?? '',
            lastname: defaultShipping?.lastname ?? '',
            save_in_address_book: 0,
          },
          shipping_carrier_code: defaultShipping?.selected_shipping_method?.carrier_code ?? '',
          shipping_method_code: defaultShipping?.selected_shipping_method?.method_code ?? '',
        })
        setHomeDeliveryShippingValues({
          country_code: defaultShipping?.country?.code as CountryCodeEnum,
          region_id: Number(defaultShipping?.region?.region_id),
          region: defaultShipping?.region?.code,
          street: [
            defaultShipping?.street?.[0] ?? '',
            defaultShipping?.street?.[1] ?? '',
            defaultShipping?.street?.[2] ?? '',
          ],
          company: defaultShipping?.company,
          telephone: defaultShipping?.telephone ?? '',
          postcode: defaultShipping?.postcode ?? '',
          city: defaultShipping?.city ?? '',
          firstname: defaultShipping?.firstname ?? '',
          lastname: defaultShipping?.lastname ?? '',
        })
      } else if (defaultUserAddress) {
        // 否则，设置默认的用户地址为 home delivery 地址
        setHomeDeliveryShippingValues({
          country_code: defaultUserAddress.country_code,
          region_id: Number(defaultUserAddress?.region?.region_id),
          region: defaultUserAddress?.region?.region_code,
          street: [
            defaultUserAddress?.street?.[0] ?? '',
            defaultUserAddress?.street?.[1] ?? '',
            defaultUserAddress?.street?.[2] ?? '',
          ],
          company: defaultUserAddress?.company,
          telephone: defaultUserAddress?.telephone ?? '',
          postcode: defaultUserAddress?.postcode ?? '',
          city: defaultUserAddress?.city ?? '',
          firstname: defaultUserAddress?.firstname ?? '',
          lastname: defaultUserAddress?.lastname ?? '',
        })
      }
    } else {
      if (defaultUserAddress) {
        // 设置默认的用户地址为 pickup 地址
        form.reset({
          firstname: defaultUserAddress.firstname,
          lastname: defaultUserAddress.lastname,
          email: '',
          telephone: defaultUserAddress.telephone ?? '',
        })
      }
    }

    // Set default billing
    const defaultUserBilling = userAddressAll.find((item) => item.default_billing)
    // 如果有用户地址且存在默认Billing
    if (defaultUserBilling) {
      setBillingValues({
        country_code: defaultUserBilling?.country_code as CountryCodeEnum,
        region_id: Number(defaultUserBilling?.region?.region_id),
        region: defaultUserBilling?.region?.region_code,
        street: [
          defaultUserBilling?.street?.[0] ?? '',
          defaultUserBilling?.street?.[1] ?? '',
          defaultUserBilling?.street?.[2] ?? '',
        ],
        company: defaultUserBilling?.company,
        telephone: defaultUserBilling?.telephone ?? '',
        postcode: defaultUserBilling?.postcode ?? '',
        city: defaultUserBilling?.city ?? '',
        firstname: defaultUserBilling?.firstname ?? '',
        lastname: defaultUserBilling?.lastname ?? '',
      })
    } else if (billingAddresses) {
      // 设置为购物车的Billing地址
      setBillingValues({
        country_code: billingAddresses?.country?.code as CountryCodeEnum,
        region_id: Number(billingAddresses?.region?.region_id),
        region: billingAddresses?.region?.code,
        street: [
          billingAddresses?.street?.[0] ?? '',
          billingAddresses?.street?.[1] ?? '',
          billingAddresses?.street?.[2] ?? '',
        ],
        company: billingAddresses?.company,
        telephone: billingAddresses?.telephone ?? '',
        postcode: billingAddresses?.postcode ?? '',
        city: billingAddresses?.city ?? '',
        firstname: billingAddresses?.firstname ?? '',
        lastname: billingAddresses?.lastname ?? '',
      })
    }
  }, [
    billingAddresses,
    form,
    isHomeDelivery,
    isRender,
    shippingAddresses,
    userAddressAll,
    saveDefaultTotalsInformation,
    checkoutEmail,
  ])

  return {
    form,
    isPaymentStep,
    guestEmailFormRef,
    billingFormRef,
    shippingFormRef,
    handleNext,
    handleBack,
    billingValues,
    homeDeliveryShippingValues,
    paymentMethods,
    pickupValues,
    isPickup,
    pickupPersonInformationVisible,
    shippingVisible,
    billingVisible,
    isRender,
    addressValidator,
    closeAddressValidator,
  }
}

export default usePickupPersonInformation
